import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { type TrafficRow, api } from "../lib/api";

type TrafficResp = { items: TrafficRow[]; total: number; scope: "own" | "public" };

export function Traffic() {
	const nav = useNavigate();
	const { data, isLoading, error } = useQuery<TrafficResp>({
		queryKey: ["traffic"],
		queryFn: () => api.get("/traffic"),
	});

	if (isLoading) return <div className="p-6">Loading…</div>;
	if (error) return <div className="p-6 text-red-700">Error: {(error as Error).message}</div>;
	const items = data?.items ?? [];

	return (
		<div className="p-6">
			<div className="mb-4 flex items-center justify-between">
				<h1 className="text-xl font-semibold">
					Traffic profiles ({data?.scope === "own" ? "yours" : "public"})
				</h1>
				{data?.scope === "own" && (
					<button
						type="button"
						className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700"
						onClick={() => nav("/traffic/new")}
					>
						+ New profile
					</button>
				)}
			</div>
			<table className="w-full text-sm">
				<thead className="text-left text-neutral-500">
					<tr>
						<th className="py-1">Name</th>
						<th>Vertical</th>
						<th>Geos</th>
						<th>Sources</th>
						<th>Volume/mo</th>
						<th>Status</th>
					</tr>
				</thead>
				<tbody>
					{items.map((t) => (
						<tr key={t.id} className="border-t border-neutral-200 hover:bg-neutral-50">
							<td className="py-1">
								<Link to={`/traffic/${t.id}`} className="text-blue-700 hover:underline">
									{t.displayName}
								</Link>
							</td>
							<td>{t.vertical}</td>
							<td>{safeArr(t.geos).join(", ")}</td>
							<td>{safeArr(t.sources).join(", ")}</td>
							<td>{t.monthlyVolume ?? "—"}</td>
							<td>{t.status}</td>
						</tr>
					))}
					{items.length === 0 && (
						<tr>
							<td colSpan={6} className="py-4 text-center text-neutral-500">
								No traffic profiles yet.
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);
}

function safeArr(s: string | null): string[] {
	if (!s) return [];
	try {
		const v = JSON.parse(s);
		return Array.isArray(v) ? (v as string[]) : [];
	} catch {
		return [];
	}
}
